import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { SegmentedControl } from "@/components/segmented-control"
import { type AnalyticsWindow, WINDOW_VALUES } from "@/types/admin-analytics"

/**
 * OKS-66 — Time-window toggle for the Platform Analytics page.
 *
 * 3-option segmented control: 30d / 90d / all-time. URL-state driven
 * (no `localStorage` — CLAUDE.md hard rule). Toggle change triggers
 * an Inertia partial reload that re-fetches `kpis` + `growth` props
 * only (UX Spec §1 erratum locked with LQE: TRD K11 — the flat-KPI
 * shape means `kpis_window` doesn't exist as a discrete prop;
 * `only: ['kpis', 'growth']` re-runs 4 KPI counts + the 3 growth
 * series, all measured <50ms total).
 *
 * OKS-625: migrated Radix ToggleGroup → Base UI radio-group segmented
 * control (`role="radio"` + arrow-key roving preserved). Routes via
 * `adminAnalyticsPath()` (js-routes) — never a hardcoded URL.
 */
export interface AnalyticsWindowToggleProps {
  value: AnalyticsWindow
  /**
   * Callback invoked with the new window value. Page-level wiring
   * calls `router.get(adminAnalyticsPath({ window }), {}, {only:[…]})`.
   * Extracted so the toggle stays presentation-only and testable.
   */
  onChange: (next: AnalyticsWindow) => void
}

export function AnalyticsWindowToggle({
  value,
  onChange,
}: AnalyticsWindowToggleProps) {
  const { t } = useTranslation()

  return (
    <SegmentedControl
      value={value}
      // Radio-group only ever emits a valid option value and never fires
      // for the already-active option, so no empty-string / no-op guard
      // is needed (unlike the old Radix ToggleGroup).
      onValueChange={onChange}
      variant="outline"
      size="sm"
      aria-label={t("admin.analytics.window.aria_label")}
      data-testid="admin-analytics-window-toggle"
      options={WINDOW_VALUES.map((window) => ({
        value: window,
        label: t(`admin.analytics.window.${window}`),
        "data-testid": `admin-analytics-window-toggle-${window}`,
      }))}
    />
  )
}

/**
 * Convenience wrapper — invokes the standard Inertia partial reload
 * with the locked `only` keys. Use this as the `onChange` handler in
 * the page when no custom handling is needed (the common case).
 *
 * Lives here so the toggle module owns the partial-reload contract
 * end-to-end; the page just passes this in.
 */
export function defaultWindowChangeHandler(
  adminAnalyticsPathHelper: (params: { window: string }) => string,
) {
  return (next: AnalyticsWindow) => {
    router.get(
      adminAnalyticsPathHelper({ window: next }),
      {},
      {
        only: ["kpis", "growth"],
        preserveScroll: true,
        preserveState: false,
      },
    )
  }
}
